import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { ICacheChanges } from '../models/cacheChanges/cacheChanges.interface';
import { IntervalService } from './interval.service';
import { UtilityService } from './util/utility.service';
import { ConstantValues } from '../models/util/constantValues';
import { DeployParams } from '../deployParams';
import { CacheChangesProviderService } from './providers/caheChanges.provider.service';
import { UserActivityService } from './userActivity.service';
import { ModelMapperService } from './util/model.mapper.service';
import { LocaleHelper } from 'src/locale/LocaleHelper';
import { UserService } from './user.service';
import { StorageService } from './util/storage.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class CacheChangesService {
  private eventsInTicketPM: number[] = [];
  private eventsInTicketLive: number[] = [];
  private selectedLiveSports: number[] = [];
  private selectedPMSports: number[] = [];
  private selectedLiveCompetitions: number[] = [];
  private selectedPMCompetitions: number[] = [];
  private ignoreFilterEventsPM: number[] = [];
  private ignoreFilterEventsLive: number[] = [];
  private isPollingLivee = false;
  private isPollingPMM = false;
  private intervalUpdaterHandleLive: NodeJS.Timeout;
  private intervalUpdaterHandlePM: NodeJS.Timeout;
  private lastUpdateNumberLive: string = null;
  private lastUpdateNumberPM: string = null;
  private isFirstCallLive = true;
  private isFirstCallPM = true;
  private cacheUpdatedLiveSource = new Subject<ICacheChanges>();
  private cacheUpdatedPMSource = new Subject<ICacheChanges>();
  private casheBackEventsLive: number[] = [];
  private casheBackEventsPM: number[] = [];
  private expandedLiveEventId: number = null;
  private expandedPMEventId: number = null;
  private languageCulture: string;
  private getAllChangesPM = false;
  private getAllChangesLive = false;
  private dateToStringFormat: Intl.DateTimeFormatOptions = { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit' };

  cacheUpdatedLive$ = this.cacheUpdatedLiveSource.asObservable();
  cacheUpdatedPM$ = this.cacheUpdatedPMSource.asObservable();

  constructor(
    private intervalService: IntervalService,
    private cacheChangesProvider: CacheChangesProviderService,
    private userActivityService: UserActivityService,
    private userService: UserService,
    private modelMapper: ModelMapperService,
    private storage: StorageService) {
    this.languageCulture = LocaleHelper.cultureName[storage.language];
    userActivityService.stopAllComunication$.subscribe(() => {
      clearInterval(this.intervalUpdaterHandlePM);
      clearInterval(this.intervalUpdaterHandleLive);
    });
  }

  public get isFirstChangesLive(): boolean{
      return this.isFirstCallLive;
  }

  public get isFirstChangesPM(): boolean{
    return this.isFirstCallPM;
}

  isPollingLive() {
    return this.isPollingLivee;
  }

  isPollingPM() {
    return this.isPollingPMM;
  }

  subscribeForAllChangesPm(){
    this.getAllChangesPM = true;
    this.updatePMServiceState();
  }

  unsubscribeForAllChangesPm(){
    this.getAllChangesPM = false;
    this.updatePMServiceState();
  }

  subscribeForAllChangesLive(){
    this.getAllChangesLive = true;
    this.updateLiveServiceState();
  }

  unsubscribeForAllChangesLive(){
    this.getAllChangesLive = false;
    this.updateLiveServiceState();
  }

  subscribeForAllEventChanges(eventId: number, isLive: boolean) {
    if (isLive) {
      const eventIndex = this.ignoreFilterEventsLive.indexOf(eventId);

      if (eventIndex === -1) {
        this.ignoreFilterEventsLive.push(eventId);
        this.updateLiveServiceState();
      }
    } else {
      const eventIndex = this.ignoreFilterEventsPM.indexOf(eventId);

      if (eventIndex === -1) {
        this.ignoreFilterEventsPM.push(eventId);
        this.updatePMServiceState();
      }
    }
  }

  unsubscribeForAllEventChanges(eventId: number, isLive: boolean) {
    if (isLive) {
      const eventIndex = this.ignoreFilterEventsLive.indexOf(eventId);
      if (eventIndex > -1) {
        this.ignoreFilterEventsLive.splice(eventIndex, 1);
        this.updateLiveServiceState();
      }
    } else {
      const eventIndex = this.ignoreFilterEventsPM.indexOf(eventId);
      if (eventIndex > -1) {
        this.ignoreFilterEventsPM.splice(eventIndex, 1);
        this.updatePMServiceState();
      }
    }
  }

  addAllEventsChangesSubscription(eventIds: number[], isLive: boolean) {
    if (isLive) {
      this.ignoreFilterEventsLive = this.ignoreFilterEventsLive.concat(eventIds.filter((eid) => !this.ignoreFilterEventsLive.includes(eid)));
      this.updateLiveServiceState();
    } else {
      this.ignoreFilterEventsPM = this.ignoreFilterEventsPM.concat(eventIds.filter((eid) => !this.ignoreFilterEventsPM.includes(eid)));
      this.updatePMServiceState();
    }
  }

  removeAllEventsChangesSubscription(isLive: boolean) {
    if (isLive) {
      this.ignoreFilterEventsLive = [];
      this.updateLiveServiceState();
    } else {
      this.ignoreFilterEventsPM = [];
      this.updatePMServiceState();
    }
  }

  subscribeForSportChanges(sportId: number, isLive: boolean) {
    if (isLive) {
      const sportIndex = this.selectedLiveSports.indexOf(sportId);

      if (sportIndex === -1) {
        this.selectedLiveSports.push(sportId);
        this.updateLiveServiceState();
      }
    } else {
      const sportIndex = this.selectedPMSports.indexOf(sportId);

      if (sportIndex === -1) {
        this.selectedPMSports.push(sportId);
        this.updatePMServiceState();
      }
    }
  }

  unsubscribeForSportChanges(sportId: number, isLive: boolean) {
    if (isLive) {
      const sportIndex = this.selectedLiveSports.indexOf(sportId);
      if (sportIndex > -1) {
        this.selectedLiveSports.splice(sportIndex, 1);
        this.updateLiveServiceState();
      }
    } else {
      const sportIndex = this.selectedPMSports.indexOf(sportId);
      if (sportIndex > -1) {
        this.selectedPMSports.splice(sportIndex, 1);
        this.updatePMServiceState();
      }
    }
  }

  removeSportsSubscribtion(isLive: boolean) {
    if (isLive) {
      this.selectedLiveSports = [];
      this.updateLiveServiceState();
    } else {
      this.selectedPMSports = [];
      this.updatePMServiceState();
    }
  }

  addSportsSubscribtion(selectedSports: number[], isLive: boolean) {
    if (isLive) {
      this.selectedLiveSports = this.selectedLiveSports.concat(selectedSports.filter((sid) => this.selectedLiveSports.indexOf(sid) < 0));
      this.updateLiveServiceState();
    } else {
      this.selectedPMSports = this.selectedPMSports.concat(selectedSports.filter((sid) => this.selectedPMSports.indexOf(sid) < 0));
      this.updatePMServiceState();
    }
  }


  subscribeForCompetitionChanges(competitionId: number, isLive: boolean) {
    if (isLive) {
      const competitionIndex = this.selectedLiveCompetitions.indexOf(competitionId);

      if (competitionIndex === -1) {
        this.selectedLiveCompetitions.push(competitionId);
        this.updateLiveServiceState();
      }
    } else {
      const competitionIndex = this.selectedPMCompetitions.indexOf(competitionId);

      if (competitionIndex === -1) {
        this.selectedPMCompetitions.push(competitionId);
        this.updatePMServiceState();
      }
    }
  }

  unsubscribeForCompetitionChanges(competitionId: number, isLive: boolean) {
    if (isLive) {
      const competitionIndex = this.selectedLiveCompetitions.indexOf(competitionId);

      if (competitionIndex > -1) {
        this.selectedLiveCompetitions.splice(competitionIndex, 1);

        this.updateLiveServiceState();
      }
    } else {
      const competitionIndex = this.selectedPMCompetitions.indexOf(competitionId);

      if (competitionIndex > -1) {
        this.selectedPMCompetitions.splice(competitionIndex, 1);

        this.updatePMServiceState();
      }
    }
  }

  removeCompetitionsSubscribtion(isLive: boolean) {
    if (isLive) {
      this.selectedLiveCompetitions = [];
      this.updateLiveServiceState();
    } else {
      this.selectedPMCompetitions = [];
      this.updatePMServiceState();
    }
  }

  addCompetitionsSubscribtion(selectedCompetitions: number[], isLive: boolean = false) {
    if (isLive) {
      this.selectedLiveCompetitions = this.selectedLiveCompetitions.concat(selectedCompetitions.filter((cid) => this.selectedLiveCompetitions.indexOf(cid) < 0));
      this.updateLiveServiceState();
    } else {
      this.selectedPMCompetitions = this.selectedPMCompetitions.concat(selectedCompetitions.filter((cid) => this.selectedPMCompetitions.indexOf(cid) < 0));
      this.updatePMServiceState();
    }
  }

  subscribeForEventInTicketChanges(eventId: number, isLive: boolean) {
    if (isLive) {
      const eventIndex = this.eventsInTicketLive.indexOf(eventId);
      if (eventIndex === -1) {
        this.eventsInTicketLive.push(eventId);
        this.updateLiveServiceState();
      }
    } else {
      const eventIndex = this.eventsInTicketPM.indexOf(eventId);
      if (eventIndex === -1) {
        this.eventsInTicketPM.push(eventId);
        this.updatePMServiceState();
      }
    }
  }

  unsubscribeForEventInTicketChanges(eventId: number, isLive: boolean) {
    if (isLive) {
      const eventIndex = this.eventsInTicketLive.indexOf(eventId);
      if (eventIndex > -1) {
        this.eventsInTicketLive.splice(eventIndex, 1);
        this.updateLiveServiceState();
      }
    } else {
      const eventIndex = this.eventsInTicketPM.indexOf(eventId);
      if (eventIndex > -1) {
        this.eventsInTicketPM.splice(eventIndex, 1);
        this.updatePMServiceState();
      }
    }
  }

  removeEventsInTicketSubscribtion() {
    this.eventsInTicketLive = [];
    this.eventsInTicketPM = [];
    this.updateLiveServiceState();
    this.updatePMServiceState();
  }

  addEventsSubscribtion(events: number[], isLive: boolean) {
    if (isLive) {
      this.eventsInTicketLive = this.eventsInTicketLive.concat(events.filter((eid) => this.eventsInTicketLive.indexOf(eid) < 0));
      this.updateLiveServiceState();
    } else {
      this.eventsInTicketPM = this.eventsInTicketPM.concat(events.filter((eid) => this.eventsInTicketPM.indexOf(eid) < 0));
      this.updatePMServiceState();
    }
  }

  subscriebeForReloadedEventInTicket(eventId: number, isLive: boolean) {
    if (isLive) {
      this.eventsInTicketLive.push(eventId);
      this.updateLiveServiceState();
    } else {
      this.eventsInTicketPM.push(eventId);
      this.updatePMServiceState();
    }
  }

  unsubscribeFromCasheBack() {
    this.casheBackEventsLive = [];
    this.casheBackEventsPM = [];
    this.updatePMServiceState();
    this.updateLiveServiceState();
  }

  subscriebeForCasheBackEvent(eventId: number, isLive: boolean) {
    if (isLive) {
      const liveEventIndex = this.casheBackEventsLive.indexOf(eventId);
      if (liveEventIndex === -1) {
        this.casheBackEventsLive.push(eventId);
      }
      this.updateLiveServiceState();
    } else {
      const eventIndex = this.casheBackEventsPM.indexOf(eventId);
      if (eventIndex === -1) {
        this.casheBackEventsPM.push(eventId);
      }
      this.updatePMServiceState();
    }
  }

  subscribeForEventExpanded(eventId: number, isLive: boolean) {
    if (isLive) {
      this.subscribeForLiveEventExpanded(eventId);
    } else {
      this.subscribeForPMEventExpanded(eventId);
    }
  }

  unsubscribeForEventExpanded(isLive: boolean) {
    if (isLive) {
      this.unsubscribeForLiveEventExpanded();
    } else {
      this.unsubscribeForPMEventExpanded();
    }
  }


  unsubscribeForExpanded() {
    this.unsubscribeForLiveEventExpanded();
    this.unsubscribeForPMEventExpanded();
  }

  private subscribeForLiveEventExpanded(eventId: number) {
    this.expandedLiveEventId = eventId;
  }

  private unsubscribeForLiveEventExpanded() {
    this.expandedLiveEventId = null;
  }

  private subscribeForPMEventExpanded(eventId: number) {
    this.expandedPMEventId = eventId;
  }

  private unsubscribeForPMEventExpanded() {
    this.expandedPMEventId = null;
  }

  private setIsLivePolling(isPolling: boolean) {
    this.isPollingLivee = isPolling;
    if (isPolling) {
      this.updateOddsLive(this.cacheChangesProvider);
      this.intervalUpdaterHandleLive = this.intervalService.setInterval(() => { this.updateOddsLive(this.cacheChangesProvider); }, ConstantValues.CACHE_INTERVAL_LIVE_SEC * 1000);
    } else {
      this.intervalService.clearInterval(this.intervalUpdaterHandleLive);
      this.lastUpdateNumberLive = null;
      this.isFirstCallLive = true;
    }
  }

  private setIsPMPolling(isPolling: boolean) {
    this.isPollingPMM = isPolling;
    if (isPolling) {
      this.updateOddsPM(this.cacheChangesProvider);
      this.intervalUpdaterHandlePM = this.intervalService.setInterval(() => { this.updateOddsPM(this.cacheChangesProvider); }, ConstantValues.CACHE_INTERVAL_PM_SEC * 1000);
    } else {
      this.intervalService.clearInterval(this.intervalUpdaterHandlePM);
      this.lastUpdateNumberPM = null;
      this.isFirstCallPM = true;
    }
  }

  updateOddsLive(cacheChangesProvider: CacheChangesProviderService) {
    const eventsToSubscribe: Set<number> = new Set(this.eventsInTicketLive.concat(this.casheBackEventsLive).concat(this.ignoreFilterEventsLive));
    if (environment.useMinCacheChanges) {
      // MIN CACHE USED
      cacheChangesProvider.updateOddsMinimal(this.selectedLiveSports, this.selectedLiveCompetitions, [...eventsToSubscribe], ConstantValues.PAGE_ID_LIVE, this.lastUpdateNumberLive, true, this.expandedLiveEventId, this.getAllChangesLive).subscribe(tc => {
        if (tc) {
          if (tc.maxDeltaCacheNumberAsString !== '' || tc.maxDeltaCacheNumberAsString) {
            const tmp = this.lastUpdateNumberLive;
            this.lastUpdateNumberLive = tc.maxDeltaCacheNumberAsString;
            this.cacheUpdatedLiveSource.next(this.modelMapper.mapCacheChanges(tc, this.expandedLiveEventId, this.storage.ticketItemGroups, this.languageCulture, this.dateToStringFormat, {}));
            this.isFirstCallLive = false;
          } else {
            this.isFirstCallLive = false;
            this.lastUpdateNumberLive = null;
          }
        } else {
          this.isFirstCallLive = true;
          this.lastUpdateNumberLive = null;
        }
      });
    }
    // else {
    // OLD CACHE CHANGES
    // cacheChangesProvider.updateOdds(this.selectedLiveSports, this.selectedLiveCompetitions, Array.from(eventsToSubscribe), ConstantValues.PAGEIDLIVE, this.lastUpdateNumberLive, true, this.expandedLiveEventId).subscribe(tc => {
    //     if (tc) {
    //         if (tc.maxDeltaCacheNumberAsString !== '' || tc.maxDeltaCacheNumberAsString) {
    //             this.lastUpdateNumberLive = tc.maxDeltaCacheNumberAsString;
    //             this.cacheUpdatedLiveSource.next(tc);
    //         }
    //     } else {
    //         this.lastUpdateNumberLive = null;
    //         this.updateOddsLive(this.cacheChangesProvider);
    //     }
    // });
    // }
  }

  updateOddsPM(cacheChangesProvider: CacheChangesProviderService) {
    const eventsToSubscribe: Set<number> = new Set(this.eventsInTicketPM.concat(this.casheBackEventsPM).concat(this.ignoreFilterEventsPM));

    if (environment.useMinCacheChanges) {
      // MIN CACHE USED
      cacheChangesProvider.updateOddsMinimal(this.selectedPMSports, this.selectedPMCompetitions, [...eventsToSubscribe], ConstantValues.PAGE_ID_PM, this.lastUpdateNumberPM, false, this.expandedPMEventId, this.getAllChangesPM).subscribe(tc => {
        if (tc) {
          if (tc.maxDeltaCacheNumberAsString !== '' || tc.maxDeltaCacheNumberAsString) {
            this.lastUpdateNumberPM = tc.maxDeltaCacheNumberAsString;
            this.cacheUpdatedPMSource.next(this.modelMapper.mapCacheChanges(tc, this.expandedPMEventId, this.storage.ticketItemGroups, this.languageCulture, this.dateToStringFormat, {}));
            this.isFirstCallPM = false;
          } else {
            this.isFirstCallPM = false;
            this.lastUpdateNumberPM = null;
          }
        } else {
          this.isFirstCallPM = true;
          this.lastUpdateNumberPM = null;
        }
      });
    }
    // else {
    // OLD CACHE CHANGES
    // cacheChangesProvider.updateOdds(this.selectedPMSports, this.selectedPMCompetitions, Array.from(eventsToSubscribe), ConstantValues.PAGEIDPM, this.lastUpdateNumberPM, false, this.expandedPMEventId).subscribe(tc => {
    //     if (tc) {
    //         if (tc.maxDeltaCacheNumberAsString !== '' || tc.maxDeltaCacheNumberAsString) {
    //             this.lastUpdateNumberPM = tc.maxDeltaCacheNumberAsString;
    //             this.cacheUpdatedPMSource.next(tc);
    //         }
    //     } else {
    //         this.lastUpdateNumberPM = null;
    //         this.updateOddsPM(this.cacheChangesProvider);
    //     }
    // });
    // }
  }

  private updateLiveServiceState() {
    const shouldPoll: boolean = this.selectedLiveCompetitions.length > 0 || this.eventsInTicketLive.length > 0 || this.selectedLiveSports.length > 0 || this.casheBackEventsLive.length > 0 || this.expandedLiveEventId != null || this.ignoreFilterEventsLive.length > 0 || this.getAllChangesLive;
    if (this.isPollingLivee !== shouldPoll) {
      this.setIsLivePolling(shouldPoll);
    }
  }

  private updatePMServiceState() {
    const shouldPoll: boolean = this.selectedPMCompetitions.length > 0 || this.eventsInTicketPM.length > 0 || this.selectedPMSports.length > 0 || this.casheBackEventsPM.length > 0 || this.expandedPMEventId != null || this.ignoreFilterEventsPM.length > 0 || this.getAllChangesPM;

    if (this.isPollingPMM !== shouldPoll) {
      this.setIsPMPolling(shouldPoll);
    }
  }
}
